import { gsap, ScrollTrigger } from "gsap/all";

const COLOR_CLASSES = ["one", "two", "three"];
const SIZES = [50, 70, 100];

const VARIANTS = {
  block: {
    count: () => gsap.utils.random(2, 3, 1),
    place(item, index, { containerHeight, size }) {
      const fromEdge = gsap.utils.random(-(size + 20), -(size * 2));
      const top = Math.floor(Math.random() * containerHeight);

      item.style.top = `${top}px`;

      if (index % 2) {
        item.style.left = `${fromEdge}px`;
      } else {
        item.style.right = `${fromEdge}px`;
      }
    },
  },
  header: {
    count: () => gsap.utils.random(3, 4, 1),
    place(item, index, { containerWidth, containerHeight, size }) {
      const inset = gsap.utils.random(10, 40);
      const top = gsap.utils.random(inset, Math.max(inset, containerHeight - size - inset));

      item.style.top = `${top}px`;

      if (index % 2) {
        item.style.left = `${inset}px`;
      } else {
        item.style.right = `${inset}px`;
      }

      if (containerWidth < 600) {
        item.style.opacity = "0.7";
      }
    },
  },
  page: {
    count: () => gsap.utils.random(7, 9, 1),
    place(item, index, { containerWidth, size, count }) {
      const verticalSteps = count + 1;
      const topPercent = ((index + 1) / verticalSteps) * 100;
      const horizontalJitter = gsap.utils.random(-4, 4);
      const fromEdge = gsap.utils.random(-(size * 0.6), -(size * 1.4));

      item.style.top = `${topPercent}%`;
      gsap.set(item, { yPercent: -50 });

      if (index % 2) {
        item.style.left = `${fromEdge + horizontalJitter}px`;
      } else {
        item.style.right = `${fromEdge + horizontalJitter}px`;
      }

      if (containerWidth < 768) {
        item.style.opacity = "0.65";
      }
    },
  },
};

function getVariant(container) {
  if (container.classList.contains("has-ornaments--page")) {
    return "page";
  }

  if (container.classList.contains("has-ornaments--header")) {
    return "header";
  }

  return "block";
}

function createOrnament(index) {
  const item = document.createElement("li");
  const size = gsap.utils.random(SIZES);
  const colorClass = COLOR_CLASSES[index % COLOR_CLASSES.length];

  item.classList.add(colorClass);
  gsap.set(item, {
    width: size,
    height: size,
    rotation: 45,
  });

  return { item, size };
}

function animateOrnament(item, container, variant, reducedMotion) {
  if (reducedMotion) {
    return;
  }

  const containerHeight = container.offsetHeight || window.innerHeight;
  const yTravel = containerHeight * gsap.utils.random(0.15, 0.7);
  const xDrift = gsap.utils.random(-30, 30);
  const rotationDrift = gsap.utils.random(-15, 15);
  const scrub = gsap.utils.random(0.4, 1.4);

  gsap.to(item, {
    y: -yTravel,
    x: xDrift,
    rotation: `+=${rotationDrift}`,
    ease: "none",
    scrollTrigger: {
      trigger: container,
      scrub,
      start: "top bottom",
      end: "bottom top",
    },
  });
}

function initContainer(container, reducedMotion) {
  if (container.querySelector(".ornaments")) {
    return;
  }

  const variant = getVariant(container);
  const preset = VARIANTS[variant];
  const ornaments = document.createElement("ul");

  ornaments.classList.add("ornaments");
  ornaments.setAttribute("aria-hidden", "true");
  container.appendChild(ornaments);

  const count = preset.count();
  const containerWidth = container.offsetWidth;
  const containerHeight = container.offsetHeight;

  for (let i = 0; i < count; i++) {
    const { item, size } = createOrnament(i);

    preset.place(item, i, { containerWidth, containerHeight, size, count });
    ornaments.appendChild(item);
    animateOrnament(item, container, variant, reducedMotion);
  }
}

let resizeTimer;

function refreshOnResize() {
  window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => {
    ScrollTrigger.refresh();
  }, 200);
}

export default function initOrnaments() {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const containers = document.querySelectorAll(".has-ornaments");

  containers.forEach((container) => {
    initContainer(container, reducedMotion);
  });

  if (!reducedMotion && containers.length) {
    window.addEventListener("resize", refreshOnResize);
  }
}
